import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Tracks the `clientWidth` of an element's `offsetParent` (its nearest
 * positioned ancestor), kept current via a `ResizeObserver`.
 *
 * Used to center absolutely-positioned overlays within their real container
 * instead of estimating from `window.innerWidth` minus assumed chrome — the
 * measured value already reflects sidebars/panels that shrink the container.
 *
 * `useLayoutEffect` measures before paint so the first frame is positioned
 * correctly (no fallback flash). `enabled` lets callers skip observation when
 * the element is not absolutely positioned (e.g. a `position: fixed` mobile
 * variant whose `offsetParent` is null); toggling it re-runs the effect so the
 * observer re-attaches to the new `offsetParent` after a layout-mode switch.
 *
 * @param ref - ref to the element whose `offsetParent` width is measured
 * @param enabled - when false, observation is skipped and 0 is returned
 * @returns the offset parent's current `clientWidth`, or 0 when unavailable
 */
export const useOffsetParentWidth = (
  ref: RefObject<HTMLElement | null>,
  enabled = true
): number => {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) return;
    const parent = ref.current?.offsetParent;
    if (!(parent instanceof HTMLElement)) return;

    // Short-circuit when the width is unchanged so height-only resizes (or
    // repeated observer ticks) don't trigger redundant re-renders.
    const updateWidth = (): void =>
      setWidth((prev) =>
        prev === parent.clientWidth ? prev : parent.clientWidth
      );
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [ref, enabled]);

  return width;
};
